import Dialog, { DialogProps } from '@mui/material/Dialog';
import Drawer from '@mui/material/Drawer';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { useForkRef } from '@mui/material/utils';
import React, { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import useBreakpoints from './useBreakpoints';

export type ModalProps = Omit<DialogProps, 'open' | 'children'> & {
  anchor?: 'top' | 'bottom';
  useSwipeableDrawer?: boolean;
};

interface IModalContext {
  openModal: (modalContent: React.ReactElement, props?: ModalProps) => void;
  closeModal: () => void;
}

const ModalContext = createContext<IModalContext | null>(null);

interface ProviderProps {
  children: React.ReactElement;
}

export const ModalProvider = ({ children }: ProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<React.ReactElement | null>(null);
  const [props, setProps] = useState<ModalProps | undefined>(undefined); // TODO: Remove and handle through the theme config
  const paperRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useBreakpoints();

  const openModal = (modalContent: React.ReactElement, props?: ModalProps) => {
    setIsOpen(true);
    setContent(modalContent);
    setProps(props);
  };

  const closeModal = () => {
    setIsOpen(false);
    setContent(null);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const { anchor, useSwipeableDrawer, onClose, slotProps, fullWidth, maxWidth, ...modalProps } = props ?? {};
  const shouldRenderAnchoredDrawer = Boolean(isMobile && anchor);
  const shouldRenderSwipeableDrawer = Boolean(shouldRenderAnchoredDrawer && useSwipeableDrawer);
  const paperSlotProps = typeof slotProps?.paper === 'function' ? undefined : slotProps?.paper;
  const handlePaperRef = useForkRef(paperRef, paperSlotProps?.ref as React.Ref<HTMLElement> | undefined);

  const handleSwipeableDrawerRef = useCallback(
    (drawerRoot: HTMLDivElement | null) => {
      if (!drawerRoot) return;

      const swipeablePaper = drawerRoot.querySelector<HTMLDivElement>('.MuiDrawer-paper');
      handlePaperRef?.(swipeablePaper);

      return () => {
        handlePaperRef?.(null);
      };
    },
    [handlePaperRef]
  );

  useLayoutEffect(() => {
    if (isOpen && paperRef.current) {
      paperRef.current.scrollTop = 0;
    }
  }, [content, isOpen]);

  const handleClose: DialogProps['onClose'] = (event, reason) => {
    onClose?.(event, reason);
    closeModal();
  };

  const handleSwipeableClose = (event: React.SyntheticEvent) => {
    onClose?.(event, 'backdropClick');
    closeModal();
  };

  const drawerProps = {
    anchor,
    open: isOpen,
    onClose: handleClose,
    slotProps: {
      ...slotProps,
      paper: {
        ...(paperSlotProps ?? {
          sx: {
            maxHeight: '90vh',
          },
        }),
        ref: handlePaperRef,
      },
    },
    ...modalProps,
  };
  const { ref: _paperSlotRef, ...swipeablePaperSlotProps } = drawerProps.slotProps.paper;

  const dialogSlotProps = {
    ...slotProps,
    paper: {
      ...paperSlotProps,
      ref: handlePaperRef,
    },
  };

  const swipeableDrawerProps = {
    ...drawerProps,
    slotProps: {
      ...drawerProps.slotProps,
      // MUI's SwipeableDrawer needs to own the paper ref to track touch gestures.
      paper: swipeablePaperSlotProps,
    },
  };

  const modalContent = shouldRenderSwipeableDrawer ? (
    <SwipeableDrawer
      {...swipeableDrawerProps}
      ref={handleSwipeableDrawerRef}
      onClose={handleSwipeableClose}
      onOpen={handleOpen}
      disableSwipeToOpen
    >
      {content}
    </SwipeableDrawer>
  ) : shouldRenderAnchoredDrawer ? (
    <Drawer {...drawerProps}>{content}</Drawer>
  ) : (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      slotProps={dialogSlotProps}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      {...modalProps}
    >
      {content}
    </Dialog>
  );

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {modalContent}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);

  if (context === null) {
    throw new Error('useModal must be used within a Modal');
  }

  return context;
};
